import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { foodService } from '../../services/foodService';

const FoodCitySelection = () => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const data = await foodService?.getActiveCities();
      // Filter to show only Macerata
      const filteredCities = data?.filter(city => city?.name === 'Macerata') || [];
      setCities(filteredCities);
    } catch (err) {
      console.error('Error loading cities:', err);
      setError('Errore nel caricamento delle città');
    } finally {
      setLoading(false);
    }
  };

  const handleCitySelect = (cityName) => {
    navigate(`/food/${encodeURIComponent(cityName || '')}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento città...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Navbar */}
      <nav className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/home-servizi" className="flex-shrink-0">
              <img
                src="/assets/images/logo-1771244548080.png"
                alt="Cliccami Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/home-servizi" className="text-gray-700 hover:text-primary transition-colors">
                Home
              </Link>
              <a href="https://app.cliccami.net/#/food/Macerata" className="text-gray-700 hover:text-primary transition-colors">
                Ristoranti
              </a>
              <a href="mailto:cliccaminet@gmail.com" className="text-gray-700 hover:text-primary transition-colors">
                Contatti
              </a>
            </div>

            {/* Profile Button */}
            <Link
              to="/user-dashboard"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Icon name="User" size={18} />
              <span className="hidden sm:inline">Profilo</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative w-full h-[60vh] md:h-[75vh] overflow-hidden rounded-2xl">
        <img
          src="/assets/images/FoodDelivery-1769701088968.png"
          alt="Food Delivery Hero"
          className="w-full h-full object-cover"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/20"></div>
        
        {/* Hero Text */}
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12 lg:px-24">
          <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold mb-4 max-w-4xl">
            Ordina <span className="font-extrabold">DIRETTAMENTE</span> dai migliori locali della tua città
          </h1>
          <p className="text-white/90 text-base md:text-lg lg:text-xl max-w-2xl">
            Consegna veloce • Pagamento online sicuro • Supporto locale
          </p>
        </div>
      </div>

      {/* Come Funziona Section */}
      <div id="come-funziona" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
          Come funziona
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Step 1 */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              1
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Scegli il tuo locale
            </h3>
          </div>

          {/* Step 2 */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              2
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Ordina e paga online
            </h3>
          </div>

          {/* Step 3 */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              3
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Ricevi conferma su WhatsApp
            </h3>
          </div>

          {/* Step 4 */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
              4
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Il locale prepara e consegna
            </h3>
          </div>
        </div>
      </div>

      {/* Cities Grid */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {cities?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nessuna città disponibile al momento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cities?.map((city) => (
              <button
                key={city?.id}
                onClick={() => handleCitySelect(city?.name)}
                className="bg-card border border-border rounded-xl p-8 hover:border-primary hover:shadow-lg transition-all duration-300 text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon name="MapPin" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground mb-1">{city?.name}</h3>
                    <p className="text-sm text-muted-foreground">Scopri gli esercenti disponibili</p>
                  </div>
                  <Icon name="ChevronRight" size={24} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Payment Disclaimer */}
      <div className="max-w-4xl mx-auto px-6 py-8 text-center">
        <p className="text-sm text-gray-500">
          Tutti gli ordini vengono pagati online prima dell'invio al locale. Nessun pagamento alla consegna.
        </p>
      </div>
    </div>
  );
};

export default FoodCitySelection;